import React, { useState, useEffect, useRef, useId } from 'react';
import { useAuth } from '../../context/AuthContext';
import AuthPanel, { handOffTwoFactor, googleSignInEnabled } from './AuthPanel';
import HoneypotField from './HoneypotField';
import ErrorMessage from '../UI/ErrorMessage';
import PasswordInput from '../UI/PasswordInput';
import useModalA11y from '../../hooks/useModalA11y';
import { rememberReturnTo } from '../../utils/safeReturnTo';
import { inviteRef } from '../../utils/inviteRef';

const FIELD =
  'w-full rounded-[8px] border border-press-border bg-press-paper px-3 py-2.5 text-[14px] text-press-ink outline-none placeholder:text-press-muted/70 focus:border-press-ink mb-3';

const PASSWORD_CHECKS = [
  { key: 'length', label: '8+ characters', test: (v) => v.length >= 8 },
  { key: 'number', label: 'a number', test: (v) => /[0-9]/.test(v) },
  { key: 'special', label: 'a special character', test: (v) => /[!@#$%^&*]/.test(v) },
];

function emptySignupForm() {
  return { firstName: '', lastName: '', username: '', email: '', password: '', confirmPassword: '' };
}

// Same card design as the standalone /signup and /login pages — this modal
// is just those two forms in a popup instead of a page, so it shouldn't
// look like a different product. Both signup and signin are handled fully
// inline now; signup used to bounce out to the real /signup page because
// that page used to collect a whole profile (bio/university/skills/...) and
// a shortcut form here would've produced a thinner account. /signup is just
// account creation now too, so there's nothing left it does that this can't.
export default function AuthModal({ isOpen, mode, onClose, onSuccess }) {
  const formId = useId();
  const { setUser } = useAuth();
  const [currentMode, setCurrentMode] = useState(mode || 'signup');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [signinForm, setSigninForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState(emptySignupForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Selecting text near the edge of the card (e.g. dragging over the
  // autofilled email to replace it) can end the drag with the mouse past
  // the card's bounds — the resulting click's target is the backdrop, so a
  // plain onClick={onClose} there closes the whole modal mid-selection.
  // Requiring mousedown to ALSO have started on the backdrop itself (not
  // just where the drag happened to end) tells a real backdrop click apart
  // from a selection drag that merely finished over it.
  const mouseDownOnBackdrop = useRef(false);
  const panelRef = useRef(null);
  // Bot checks the server reads on signup: how long the form was open, and a
  // hidden field only a script would fill (HoneypotField.jsx).
  const openedAt = useRef(Date.now());
  const [honeypot, setHoneypot] = useState('');

  useModalA11y(panelRef, onClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      openedAt.current = Date.now();
      setHoneypot('');
      setCurrentMode(mode || 'signup');
      setShowEmailForm(false);
      setSigninForm({ email: '', password: '' });
      setSignupForm(emptySignupForm());
      setError('');
    }
  }, [isOpen, mode]);

  // react-router's useNavigate() isn't available here — this modal is
  // mounted by AuthModalProvider outside the <Router> tree (see
  // context/AuthModalContext.js) — so leaving the current page is always a
  // plain navigation, not a router push.
  function goToForgotPassword() {
    onClose();
    window.location.href = '/forgot-password';
  }

  // Every brand-new account goes through account setup — no exceptions,
  // even from a contextual signup prompt (message a freelancer, post a
  // job, ...). A contextual caller's onSuccess still matters for signing
  // IN to an existing account, since there's nothing to set up there.
  function handleAuthPanelSuccess(user, isNewUser) {
    if (isNewUser) {
      rememberReturnTo(window.location.pathname + window.location.search);
      window.location.href = '/username';
      return;
    }
    onSuccess?.(user);
  }

  function validateSignup() {
    if (!signupForm.firstName.trim()) return 'First name is required.';
    if (!signupForm.lastName.trim()) return 'Last name is required.';
    if (!signupForm.username.trim()) return 'Username is required.';
    if (signupForm.username.length < 4) return 'Username must be at least 4 characters.';
    if (!signupForm.email.trim()) return 'Email is required.';
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(signupForm.email)) return 'Please enter a valid email.';
    if (!PASSWORD_CHECKS.every((c) => c.test(signupForm.password))) {
      return 'Password must be 8+ characters with a number and special character.';
    }
    if (signupForm.password !== signupForm.confirmPassword) return 'Passwords do not match.';
    return null;
  }

  async function handleEmailSignup(e) {
    e.preventDefault();
    const err = validateSignup();
    if (err) { setError(err); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/users/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName: signupForm.firstName,
          lastName:  signupForm.lastName,
          username:  signupForm.username,
          email:     signupForm.email,
          password:  signupForm.password,
          ref:       inviteRef(),
          hp_contact: honeypot,
          formElapsedMs: Date.now() - openedAt.current,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Signup failed.');
      setUser(data.user);
      // Same rule as Google above — a brand-new account always goes
      // through account setup, even mid-way through a contextual flow.
      rememberReturnTo(window.location.pathname + window.location.search);
      window.location.href = '/get-started';
    } catch (err) {
      setError(err.message || 'We couldn’t create your account. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailSignin(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/users/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: signinForm.email, password: signinForm.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'We couldn’t sign you in. Check your connection and try again.');
      // 2FA account: the password matched but no session was issued, and this
      // response carries no user. Signing in here used to call setUser(undefined)
      // and report success, which left the modal "signed in" with no session.
      if (data.twoFactorRequired) { handOffTwoFactor(data.partialToken); return; }
      setUser(data.user);
      onSuccess?.(data.user);
    } catch (err) {
      setError(err.message || 'We couldn’t sign you in. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  const isSignup = currentMode === 'signup';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-press-ink/50 px-4 py-10 backdrop-blur-sm sm:py-14"
      onMouseDown={(e) => { mouseDownOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (mouseDownOnBackdrop.current && e.target === e.currentTarget) onClose();
        mouseDownOnBackdrop.current = false;
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={isSignup ? 'Create your account' : 'Welcome back'}
        tabIndex={-1}
        className="relative my-auto w-full max-w-[400px] flex-none rounded-[14px] border border-press-border bg-press-card p-8 shadow-2xl font-academy"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-5 top-5 text-xl leading-none text-press-muted transition hover:text-press-ink"
        >
          ×
        </button>

        <p className="mb-4 text-center font-press-display text-lg text-press-ink" translate="no">UniFreelancer</p>
        <h2 className="text-center font-press-display text-xl font-normal text-press-ink">
          {isSignup ? 'Create your account' : 'Welcome back'}
        </h2>
        <p className="mt-1 mb-6 text-center text-[13px] text-press-muted">
          {isSignup ? 'Join university freelancers and clients, alumni included.' : 'Please enter your details to sign in.'}
        </p>

        <AuthPanel mode={currentMode} onSuccess={handleAuthPanelSuccess} onError={setError} />

        {isSignup && !showEmailForm && (
          <>
            {googleSignInEnabled && (
              <div className="my-4 flex items-center gap-3 text-[12px] text-press-muted">
                <span className="h-px flex-1 bg-press-border" />
                or
                <span className="h-px flex-1 bg-press-border" />
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowEmailForm(true)}
              className="w-full text-center text-sm font-semibold text-press-brass-ink hover:underline"
            >
              Sign up with email
            </button>
          </>
        )}

        {!isSignup && googleSignInEnabled && (
          <div className="my-4 flex items-center gap-3 text-[12px] text-press-muted">
            <span className="h-px flex-1 bg-press-border" />
            or
            <span className="h-px flex-1 bg-press-border" />
          </div>
        )}

        {isSignup ? showEmailForm && (
          <form onSubmit={handleEmailSignup} className="mt-4">
            <HoneypotField value={honeypot} onChange={setHoneypot} />
            <label htmlFor={`${formId}-first-name`} className="mb-1.5 block text-xs font-semibold text-press-ink">First name</label>
            <input name="firstName" autoComplete="given-name" id={`${formId}-first-name`} className={FIELD} type="text" placeholder="First name"
              value={signupForm.firstName} onChange={(e) => setSignupForm((f) => ({ ...f, firstName: e.target.value }))} />

            <label htmlFor={`${formId}-last-name`} className="mb-1.5 block text-xs font-semibold text-press-ink">Last name</label>
            <input name="lastName" autoComplete="family-name" id={`${formId}-last-name`} className={FIELD} type="text" placeholder="Last name"
              value={signupForm.lastName} onChange={(e) => setSignupForm((f) => ({ ...f, lastName: e.target.value }))} />

            <label htmlFor={`${formId}-username`} className="mb-1.5 block text-xs font-semibold text-press-ink">Username</label>
            <input name="username" autoComplete="username" spellCheck={false} id={`${formId}-username`} className={FIELD} type="text" placeholder="4–24 characters" minLength={4} maxLength={24}
              value={signupForm.username} onChange={(e) => setSignupForm((f) => ({ ...f, username: e.target.value }))} />

            <label htmlFor={`${formId}-email`} className="mb-1.5 block text-xs font-semibold text-press-ink">Email</label>
            <input name="email" autoComplete="email" spellCheck={false} id={`${formId}-email`} className={FIELD} type="email" placeholder="your@email.com"
              value={signupForm.email} onChange={(e) => setSignupForm((f) => ({ ...f, email: e.target.value }))} />

            <label htmlFor={`${formId}-password`} className="mb-1.5 block text-xs font-semibold text-press-ink">Password</label>
            <PasswordInput id={`${formId}-password`} className={`${FIELD} mb-1.5`} placeholder="••••••••"
              value={signupForm.password} onChange={(e) => setSignupForm((f) => ({ ...f, password: e.target.value }))} />
            <div className="mb-3 flex flex-wrap gap-x-3 gap-y-1">
              {PASSWORD_CHECKS.map((c) => {
                const ok = c.test(signupForm.password);
                return (
                  <span key={c.key} className={`text-[12px] ${ok ? 'text-success' : 'text-press-muted'}`}>
                    {ok ? '✓' : '○'} {c.label}
                  </span>
                );
              })}
            </div>

            <label htmlFor={`${formId}-confirm-password`} className="mb-1.5 block text-xs font-semibold text-press-ink">Confirm password</label>
            <PasswordInput id={`${formId}-confirm-password`} className={FIELD} placeholder="••••••••"
              value={signupForm.confirmPassword} onChange={(e) => setSignupForm((f) => ({ ...f, confirmPassword: e.target.value }))} />

            <ErrorMessage className="mb-3">{error}</ErrorMessage>

            <button
              type="submit"
              disabled={loading}
              className="mt-1 w-full rounded-[8px] bg-press-ink px-5 py-2.5 text-sm font-bold text-press-paper transition hover:bg-press-ink/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Creating account…' : 'Create Account →'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleEmailSignin}>
            <label htmlFor={`${formId}-email-2`} className="mb-1.5 block text-xs font-semibold text-press-ink">Email</label>
            <input name="email" autoComplete="email" spellCheck={false} id={`${formId}-email-2`} className={FIELD} type="email" placeholder="your@email.com"
              value={signinForm.email} onChange={(e) => setSigninForm((f) => ({ ...f, email: e.target.value }))} />

            <label htmlFor={`${formId}-password-2`} className="mb-1.5 block text-xs font-semibold text-press-ink">Password</label>
            <PasswordInput id={`${formId}-password-2`} className={FIELD} placeholder="••••••••"
              value={signinForm.password} onChange={(e) => setSigninForm((f) => ({ ...f, password: e.target.value }))} />

            <p className="-mt-1 mb-3 text-right">
              <button type="button" onClick={goToForgotPassword} className="text-xs font-semibold text-press-brass-ink hover:underline">
                Forgot password?
              </button>
            </p>

            <ErrorMessage className="mb-3">{error}</ErrorMessage>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-[8px] bg-press-ink px-5 py-2.5 text-sm font-bold text-press-paper transition hover:bg-press-ink/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        )}

        <p className="mt-4 text-center text-[13px] text-press-muted">
          {isSignup ? (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => { setCurrentMode('signin'); setShowEmailForm(false); setError(''); }} className="font-semibold text-press-brass-ink hover:underline">
                Sign in
              </button>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <button type="button" onClick={() => { setCurrentMode('signup'); setShowEmailForm(false); setError(''); }} className="font-semibold text-press-brass-ink hover:underline">
                Sign up
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
